import * as THREE from 'three';

import AbstractObject from '../misc/object';

import {
  addDistanceLabel,
  getEndpoints,
  getSides,
  getSharpLevel,
  unitTransform,
  generatePlanarUVMap
} from '@services/helper.service';

import { HTMLEditableLabel } from './html-label';
import Line from './line';
import Arrow from './arrow';

import { concreteMaterial, GetMasonryMaterial } from '../materials/ground';

import Scene from '@scene';

import { defaultUnits } from '@config';
import { standardList } from '../../app.values';
import { APP_TEXTS } from '@texts';
import Panel from './panel';
import { Screw } from './screw';

const maxEdgeSize = 20;
export const LabelsPadding = 35;

export default class Ground extends AbstractObject {

  static types = {
    BASE: 'base',
    FLAT: 'flat'
  };

  constructor(options, screwCoords, globalOptions) {
    super(options);

    let ground = null;
    var geometry = null;
    if (screwCoords) {
      this.screwPositions = getEndpoints(screwCoords);
      this.screwRadius = globalOptions.screw.radius;
      this.standOff = globalOptions.screw.heightType === Screw.types.height.INDENTED ? globalOptions.panel.standOff.value : 0;

      this.groundPadding = getSides(globalOptions, this.screwPositions);

      this.width = this.groundPadding.left + this.groundPadding.right;
      this.height = this.groundPadding.top + this.groundPadding.bottom;
      if (globalOptions.ground.maxDepth) {
        this._maxDepth = globalOptions.ground.maxDepth;
      } else {
        switch (globalOptions.panel.type) {
          case Panel.types.SQUARE: 
          case Panel.types.TRIANGLE: {
            this._maxDepth = 2 * Math.max(globalOptions.panel.width, globalOptions.panel.height);
            break;
          }
          case Panel.types.CUSTOM: {
            if (globalOptions.panel.vertices.positions.length < 3) {
              this._maxDepth = 2 * globalOptions.panel.width;
            } else {
              let panelEndpoints = getEndpoints(globalOptions.panel.vertices.positions);
              this._maxDepth = 2 * Math.max(
                Math.abs(panelEndpoints.left.y - panelEndpoints.right.y), 
                Math.abs(panelEndpoints.bottom.z - panelEndpoints.top.z)
              );
            }
            break;
          }
          case Panel.types.CIRCLE: 
          default: {
            this._maxDepth = 2 * globalOptions.panel.width;
            break;
          }
        }
      }

      geometry = this.getGeometry(this.width, this.height, Math.min(this.options.depth, this.maxDepth));
      geometry = generatePlanarUVMap(geometry);

      if (globalOptions && Number(globalOptions.designStandard) === standardList['Masonry']) 
        ground = new THREE.Mesh(geometry, GetMasonryMaterial(options.masonryTextureColor));
      // else if (globalOptions && globalOptions.isRebar) 
      //   ground = new THREE.Mesh(geometry, masonryMaterialWhite);
      else 
        ground = new THREE.Mesh(geometry, concreteMaterial);

      // ground = new THREE.Mesh(geometry, 
      //   globalOptions ? 
      //     (Number(globalOptions.designStandard) === standardList['Masonry'] ? 
      //       GetMasonryMaterial(options.masonryTextureColor) : concreteMaterial) : concreteMaterial);
      
      // TODO: Remove it tmp rotation
      ground.rotation.y = Math.PI / 2;

      // Make spaces for left/right/top/bottom side
      ground.position.y = this.groundPadding.right - (this.width / 2);
      ground.position.z = this.groundPadding.top - (this.height / 2);
    } else if (this.options.width && this.options.height) {
      this.width = this.options.width;
      this.height = this.options.height;

      geometry = this.getGeometry(this.width, this.height, this.options.depth);
      geometry = generatePlanarUVMap(geometry);
  
      if (globalOptions && Number(globalOptions.designStandard) === standardList['Masonry']) 
        ground = new THREE.Mesh(geometry, GetMasonryMaterial(options.masonryTextureColor));
      // else if (globalOptions && globalOptions.isRebar) 
      //   ground = new THREE.Mesh(geometry, masonryMaterialWhite);
      else 
        ground = new THREE.Mesh(geometry, concreteMaterial);

      // ground = new THREE.Mesh(geometry, 
      //   globalOptions ? 
      //     (Number(globalOptions.designStandard) === standardList['Masonry'] ? 
      //       GetMasonryMaterial(options.masonryTextureColor) : concreteMaterial) : concreteMaterial);
    } else {
      throw new Error('Can\'t create ground object!');
    }

    this.object.addMesh(ground);
  }

  get size() {
    return {
      width: this.width,
      height: this.height,
      depth: this.maxDepth ? Math.min(this.options.depth, this.maxDepth) : (this.options.depth || 0)
    };
  }

  get maxDepth() {
    return this._maxDepth;
  }

  getGeometry(width, height, depth) {
    const shape = new THREE.Shape();

    width /= 2; // eslint-disable-line
    height /= 2; // eslint-disable-line

    switch (this.options.type) {
      case Ground.types.FLAT: {
        shape.moveTo(-width, -height);
        shape.lineTo(width, -height);
        shape.lineTo(width, height);
        shape.lineTo(-width, height);
        shape.lineTo(-width, -height);

        break;
      }
      case Ground.types.BASE:
      default: {
        shape.moveTo(height, -width);

        for (let i = -width; i < width - getSharpLevel(); i += getSharpLevel()) {
          const rand = typeof this.options.padding.C2y === 'number' ? 0 : Math.random() * maxEdgeSize;

          shape.lineTo(height + rand, i);
        }

        for (let i = height; i > -height + getSharpLevel(); i -= getSharpLevel()) {
          const rand = typeof this.options.padding.C1x === 'number' ? 0 : Math.random() * maxEdgeSize;

          shape.lineTo(i, width + rand);
        }

        for (let i = width; i > -width + getSharpLevel(); i -= getSharpLevel()) {
          const rand = typeof this.options.padding.C1y === 'number' ? 0 : Math.random() * maxEdgeSize;

          shape.lineTo(-height - rand, i);
        }

        for (let i = -height; i < height - getSharpLevel(); i += getSharpLevel()) {
          const rand = typeof this.options.padding.C2x === 'number' ? 0 : Math.random() * maxEdgeSize;

          shape.lineTo(i, -width - rand);
        }

        shape.lineTo(height, -width);

        break;
      }
    }

    const extrudeSettings = {
      depth,
      bevelEnabled: false
    };

    return new THREE.ExtrudeGeometry(shape, extrudeSettings);
  }

  createWidthLabel(text = 'W', options) {
    const position = {
      top: {
        from: {
          x: this.size.width / -2,
          y: this.size.height / 2,
          z: this.size.depth
        },
        to: {
          x: this.size.width / -2,
          y: this.size.height / 2 + LabelsPadding * 2,
          z: this.size.depth
        }
      },
      bottom: {
        from: {
          x: this.size.width / 2,
          y: this.size.height / 2,
          z: this.size.depth
        },
        to: {
          x: this.size.width / 2,
          y: this.size.height / 2 + LabelsPadding * 2,
          z: this.size.depth
        }
      }
    };
    const {object, text: domElement} = addDistanceLabel({
      ...position,
      size: this.size.width,
      arrowDirection: Arrow.types.direction.X,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.size.width).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.size.width).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            width: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    this.object.addMesh(object);

    return {domElement, position, object};
  }

  createHeightLabel(text = 'H', options, padding) {
    const position = {
      top: {
        from: {
          x: this.size.width / -2,
          y: this.size.height / -2,
          z: this.size.depth
        },
        to: {
          x: this.size.width / -2 + padding,
          y: this.size.height / -2,
          z: this.size.depth
        }
      },
      bottom: {
        from: {
          x: this.size.width / -2,
          y: this.size.height / 2,
          z: this.size.depth
        },
        to: {
          x: this.size.width / -2 + padding,
          y: this.size.height / 2,
          z: this.size.depth
        }
      }
    };
    const {object, text: domElement} = addDistanceLabel({
      ...position,
      size: this.size.height,
      arrowDirection: Arrow.types.direction.Y,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.size.height).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.size.height).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            height: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    this.object.addMesh(object);

    return {domElement, position, object};
  }

  // used for Rebar
  createDepthLabel(text = 'D', options) {
    const path = [{
      x: 0,
      y: 0,
      z: 0
    }];
    path.push({
      ...path[0],
      x: path[0].x + 100,// this.width / 6,
      y: path[0].y + 100, //this.height / 6,
    });
    path.push({
      ...path[1],
      x: path[1].x + 100, //this.width / 6,
    });
    const line = new Line({
      path,
      disableDepthTest: true
    });
    line.position.x = this.size.width / 2 - LabelsPadding;//this.size.width / 20;
    line.position.y = this.size.height / -2 + LabelsPadding;//this.size.height / -20;
    this.object.addObject(line);

    const domElement = new HTMLEditableLabel({
      text: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : '') + 
        `${unitTransform(+this.options.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.LEFT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      minValue: 0
    });
    domElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });
    domElement.position = new THREE.Vector3(
      line.position.x + path[1].x,
      0,
      -line.position.y - path[1].y
    );

    return {domElement, object: line, path};
  }

  // used for Rebar
  create3DDepthLabelRebar(text = 'La', options) {
    let top = {
      from: {
        x: - this.size.width / 2,
        y: - this.size.height / 2,
        z: this.position.z
      },
      to: {
        x: - this.size.width / 2 - LabelsPadding * 2,
        y: - this.size.height / 2,
        z: this.position.z
      }
    };
    let bottom = {
      from: {
        x: - this.size.width / 2,
        y: - this.size.height / 2,
        z: this.position.z + this.size.depth
      },
      to: {
        x: - this.size.width / 2 - LabelsPadding * 2,
        y: - this.size.height / 2,
        z: this.position.z + this.size.depth
      }
    };
    
    const Thickness = addDistanceLabel({
      top,
      bottom,
      size: this.size.depth,
      arrowDirection: Arrow.types.direction.Z,
      labelText: ((!options || !options.is3DMode || options.showLabels) ? `${text} = ` : "") +
        `${unitTransform(this.options.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(this.options.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      },
      arrowSize: 25
    });
    
    return {
      domElement: Thickness.text,
      object: Thickness.object
    };
  }

  // TODO: Refactoring
  get depthLabel() {
    const position = { ...this.position };
    position.x += this.size.depth;
    position.y -= this.groundPadding.left - 30; //this.size.width / 2 - 30;
    position.z += this.groundPadding.top - 30; //this.size.height / 2 - 30;

    const path = [{
      x: position.x,
      y: position.y,
      z: position.z
    }, {
      x: position.x,
      y: position.y - 80,
      z: position.z - 80
    }];

    const depthLabel = new HTMLEditableLabel({
      text: `${APP_TEXTS.BASE_PLATE_THICKNESS_LABEL.Content} = 
        ${unitTransform(this.options.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(this.options.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      minValue: 0
    });
    depthLabel.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        ground: {
          depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });
    depthLabel.position = new THREE.Vector3(
      -path[1].y,
      path[1].x,
      path[1].z
    );

    let pt =new THREE.Vector3(); pt.copy(path[1]);
    pt.setY(pt.y - depthLabel.textLabelWidth);
    path.push({...pt});

    const line = new Line({path, disableDepthTest: true });

    return {
      domElement: depthLabel,
      object: line
    };
  }
  // used for General-calculation
  get3DDepthLabel(options) {
    let top = {
      from: {
        x: 0,
        y: -this.groundPadding.left,
        z: this.groundPadding.top
      },
      to: {
        x: 0,
        y: -this.groundPadding.left - LabelsPadding,
        z: this.groundPadding.top 
      }
    };
    let bottom = {
      from: {
        x: this.size.depth,
        y: -this.groundPadding.left,
        z: this.groundPadding.top
      },
      to: {
        x: this.size.depth,
        y: -this.groundPadding.left - LabelsPadding,
        z: this.groundPadding.top
      }
    };
    
    let text = ((!options || !options.is3DMode || options.showLabels) ? `${APP_TEXTS.BASE_PLATE_THICKNESS_LABEL.Content} = ` : "") +
              `${unitTransform(this.options.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`;
    const Thickness = addDistanceLabel({
      top,
      bottom,
      size: this.size.depth,
      arrowDirection: Arrow.types.direction.X,
      labelText: text,
      labelValue: unitTransform(this.options.depth).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      }
      // disableDepthTest: false
    });

    Thickness.text.position.x = this.groundPadding.top; 
    Thickness.text.position.y = this.position.x + this.size.depth / 2;
    Thickness.text.position.z = -this.groundPadding.left - LabelsPadding;

    if (options && options.is3DMode) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(top.to.x, top.to.y, top.to.z);
        startPos.applyEuler(options.rotation);
        var endPos = new THREE.Vector3(bottom.to.x, bottom.to.y, bottom.to.z);
        endPos.applyEuler(options.rotation);
        Thickness.text.setDirection(startPos, endPos, false);
      }
      Thickness.text.position.z -= 20; // add padding
      // Thickness.text.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      // Thickness.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      // Thickness.text.align = HTMLEditableLabel.types.align.CENTER;

      // set the direction of the text only when the base plate is mounted on a wall
      // if (rotation.x === Math.PI / 2 && rotation.y === 0 && rotation.z === 0) 
    }

    return {
      domElement: Thickness.text,
      object: Thickness.object
    };
  }

  getDepthLabel(options) {
    var depth;
    if (options && options.is3DMode) {
      depth = this.get3DDepthLabel(options);
      this.object.addMesh(depth.object);
    }
    else {
      depth = this.depthLabel;
      this.object.addObject(depth.object);
    }

    return {
      htmlElement: depth.domElement,
      object: depth.object
    };
  }

  getEdgeDistanceLabels(options) {
    const edgesObject = new AbstractObject();
    const edgeLabels = {};

    const C1xLabel = this.getC1xLabel(this.screwPositions, options);
    edgeLabels.C1x = C1xLabel;
    edgesObject.object.addMesh(C1xLabel.object);
    edgesObject.htmlLayer.add(C1xLabel.text);

    const C1yLabel = this.getC1yLabel(this.screwPositions, options);
    edgeLabels.C1y = C1yLabel;
    edgesObject.object.addMesh(C1yLabel.object);
    edgesObject.htmlLayer.add(C1yLabel.text);

    const C2xLabel = this.getC2xLabel(this.screwPositions, options);
    edgeLabels.C2x = C2xLabel;
    edgesObject.object.addMesh(C2xLabel.object);
    edgesObject.htmlLayer.add(C2xLabel.text);

    const C2yLabel = this.getC2yLabel(this.screwPositions, options);
    edgeLabels.C2y = C2yLabel;
    edgesObject.object.addMesh(C2yLabel.object);
    edgesObject.htmlLayer.add(C2yLabel.text);

    return { edgesObject, edgeLabels };
  }

  getC1xLabel({ left, bottom }, options) {
    let topCoords = {
      from: {
        x: -left.x +1 - this.standOff / 2, 
        y: left.y,
        z: bottom.z
      },
      to: {
        x: -left.x +1 - this.standOff / 2,
        y: left.y,
        z: LabelsPadding + this.groundPadding.top
        // z: bottom.z + labelsPadding + (this.options.padding.C1y || 0)
      }
    };
    let bottomCoords = {
      from: {
        x: 0,
        y: 0,
        z: 0
      },
      to: {
        x: 0,
        y: 0,
        z: 0
      }
    };
    
    let infinityHTML = "font-family: cursive;font-size: 25px;top: -0.55rem;position: absolute";
    let horizontalPadding = (!options || !options.is3DMode || options.showLabels) ? ";right: 0rem" : ";left: 0rem";
    infinityHTML = "<span style=\"visibility: hidden;\">AB</span><span style=\"" + infinityHTML + horizontalPadding + "\">∞</span>";
    let text = ((!options || !options.is3DMode || options.showLabels) ? `C1x = ` : "") +
              `${Number.isFinite(this.options.padding.C1x) ? 
                unitTransform(this.options.padding.C1x).from(defaultUnits.DISTANCE).to(Scene.units.distance) : infinityHTML}`;
    const C1x = addDistanceLabel({
      top: topCoords,
      bottom: bottomCoords,
      size: this.options.padding.C1x || this.groundPadding.right - left.y,//(this.size.width / 2) - left.y,
      arrowDirection: Arrow.types.direction.Y,
      labelText: text,
      labelValue: this.options.padding.C1x && unitTransform(this.options.padding.C1x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.TOP,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C1x: value ? unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE) : null
            }
          }
        },
        false,
        didChange);
      }
    });

    if (options && options.is3DMode) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(topCoords.to.x, topCoords.to.y, topCoords.to.z);
        startPos.applyEuler(options.rotation);
        var endPos = new THREE.Vector3(topCoords.to.x, LabelsPadding + this.groundPadding.left, topCoords.to.z);
        endPos.applyEuler(options.rotation);
        C1x.text.setDirection(startPos, endPos, false);
      }
      C1x.text.position.z += 20; // add padding
      C1x.text.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      C1x.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      C1x.text.align = HTMLEditableLabel.types.align.CENTER;
    }

    return C1x;
  }

  getC1yLabel({ left, bottom }, options) {
    let topCoords = {
      from: {
        x: -left.x +1 - this.standOff / 2,
        y: left.y,
        z: bottom.z
      },
      to: {
        x: -left.x +1 - this.standOff / 2,
        y: LabelsPadding + this.groundPadding.right,
        // y: left.y + labelsPadding + (this.options.padding.C1x || 0),
        z: bottom.z
      }
    };
    let bottomCoords = {
      from: {
        x: 0,
        y: 0,
        z: 0
      },
      to: {
        x: 0,
        y: 0,
        z: 0
      }
    };
    
    let infinityHTML = "font-family: cursive;font-size: 25px;top: -0.55rem;position: absolute";
    let horizontalPadding = (!options || !options.is3DMode || options.showLabels) ? ";right: 0rem" : ";left: 0rem";
    infinityHTML = "<span style=\"visibility: hidden;\">AB</span><span style=\"" + infinityHTML + horizontalPadding + "\">∞</span>";
    let text = ((!options || !options.is3DMode || options.showLabels) ? `C1y = ` : "") +
              `${Number.isFinite(this.options.padding.C1y) ? 
                unitTransform(this.options.padding.C1y).from(defaultUnits.DISTANCE).to(Scene.units.distance) : infinityHTML}`;
    const C1y = addDistanceLabel({
      top: topCoords,
      bottom: bottomCoords,
      size: this.options.padding.C1y || this.groundPadding.top - bottom.z, //(this.size.height / 2) - bottom.z,
      arrowDirection: Arrow.types.direction.Z,
      labelText: text,
      labelValue: this.options.padding.C1y && unitTransform(this.options.padding.C1y).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C1y: value ? unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE) : null
            }
          }
        },
        false,
        didChange);
      }
    });

    if (options && options.is3DMode) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(topCoords.to.x, topCoords.to.y, topCoords.to.z);
        startPos.applyEuler(options.rotation);
        var endPos = new THREE.Vector3(topCoords.to.x, topCoords.to.y, LabelsPadding + this.groundPadding.bottom);
        endPos.applyEuler(options.rotation);
        C1y.text.setDirection(startPos, endPos, false);
      }
      C1y.text.position.x -= 20; // add padding
      C1y.text.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      C1y.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      C1y.text.align = HTMLEditableLabel.types.align.CENTER;
    }

    return C1y;
  }

  getC2xLabel({ right, bottom }, options) {
    let topCoords = {
      from: {
        x: -right.x +1 - this.standOff / 2,
        y: right.y,
        z: bottom.z
      },
      to: {
        x: -right.x +1 - this.standOff / 2,
        y: right.y,
        z: LabelsPadding + this.groundPadding.top,
        // z: bottom.z + labelsPadding + (this.options.padding.C1y || 0)
      }
    };
    let bottomCoords = {
      from: {
        x: 0,
        y: 0,
        z: 0
      },
      to: {
        x: 0,
        y: 0,
        z: 0
      }
    };
    
    let infinityHTML = "font-family: cursive;font-size: 25px;top: -0.55rem;position: absolute";
    let horizontalPadding = (!options || !options.is3DMode || options.showLabels) ? ";right: 0rem" : ";left: 0rem";
    infinityHTML = "<span style=\"visibility: hidden;\">AB</span><span style=\"" + infinityHTML + horizontalPadding + "\">∞</span>";
    let text = ((!options || !options.is3DMode || options.showLabels) ? `C2x = ` : "") +
              `${Number.isFinite(this.options.padding.C2x) ? 
                unitTransform(this.options.padding.C2x).from(defaultUnits.DISTANCE).to(Scene.units.distance) : infinityHTML}`;
    const C2x = addDistanceLabel({
      top: topCoords,
      bottom: bottomCoords,
      size: -this.options.padding.C2x || -this.groundPadding.left - right.y, // (this.size.width / -2) - right.y,
      arrowDirection: Arrow.types.direction.Y,
      labelText: text,
      labelValue: this.options.padding.C2x && unitTransform(this.options.padding.C2x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.TOP,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C2x: value ? unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE) : null
            }
          }
        },
        false,
        didChange);
      }
    });

    if (options && options.is3DMode) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(topCoords.to.x, topCoords.to.y, topCoords.to.z);
        startPos.applyEuler(options.rotation);
        var endPos = new THREE.Vector3(topCoords.to.x, - LabelsPadding - this.groundPadding.right, topCoords.to.z);
        endPos.applyEuler(options.rotation);
        C2x.text.setDirection(startPos, endPos, false);
      }
      C2x.text.position.z += 20; // add padding
      C2x.text.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      C2x.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      C2x.text.align = HTMLEditableLabel.types.align.CENTER;
    }

    return C2x;
  }

  getC2yLabel({ top, left }, options) {
    let topCoords = {
      from: {
        x: -left.x +1 - this.standOff / 2,
        y: left.y,
        z: top.z
      },
      to: {
        x: -left.x +1 - this.standOff / 2,
        // y: left.y + labelsPadding + (this.options.padding.C1x || 0),
        y: LabelsPadding + this.groundPadding.right,
        z: top.z
      }
    };
    let bottomCoords = {
      from: {
        x: 0,
        y: 0,
        z: 0
      },
      to: {
        x: 0,
        y: 0,
        z: 0
      }
    };
    
    let infinityHTML = "font-family: cursive;font-size: 25px;top: -0.55rem;position: absolute";
    let horizontalPadding = (!options || !options.is3DMode || options.showLabels) ? ";right: 0rem" : ";left: 0rem";
    infinityHTML = "<span style=\"visibility: hidden;\">AB</span><span style=\"" + infinityHTML + horizontalPadding + "\">∞</span>";
    let text = ((!options || !options.is3DMode || options.showLabels) ? `C2y = ` : "") +
              `${Number.isFinite(this.options.padding.C2y) ? 
                unitTransform(this.options.padding.C2y).from(defaultUnits.DISTANCE).to(Scene.units.distance) : infinityHTML}`;
    const C2y = addDistanceLabel({
      top: topCoords,
      bottom: bottomCoords,
      size: -this.options.padding.C2y || -this.groundPadding.bottom - top.z, //(this.size.height / -2) - top.z,
      arrowDirection: Arrow.types.direction.Z,
      labelText: text,
      labelValue: this.options.padding.C2y && unitTransform(this.options.padding.C2y).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C2y: value ? unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE) : null
            }
          }
        },
        false,
        didChange);
      }
    });

    if (options && options.is3DMode) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(topCoords.to.x, topCoords.to.y, topCoords.to.z);
        startPos.applyEuler(options.rotation);
        var endPos = new THREE.Vector3(topCoords.to.x, topCoords.to.y, - LabelsPadding - this.groundPadding.top);
        endPos.applyEuler(options.rotation);
        C2y.text.setDirection(startPos, endPos, false);
      }
      C2y.text.position.x -= 20; // add padding
      C2y.text.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      C2y.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      C2y.text.align = HTMLEditableLabel.types.align.CENTER;
    }

    return C2y;
  }

}



// WEBPACK FOOTER //
// ./src/object-view/objects/ground.js